import { Address, Checkout, CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { validateCountry } from "@lib/utils/countryUtility";
import { Text } from "@ecco/ui/text";
import { Divider } from "@ecco/ui/divider";
import { Box } from "@ecco/ui/box";
import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useRef } from "react";
import { styles } from "./DeliveryStepFormStyles";
import AddressFormFields from "./AddressFormFields";
import { getStatesByCountry } from "@components/commerce/baseForms/helpers/helpers";
import { activeSiteConfig } from "@config/site/site-config";
import { BILLING_PREFIX } from "@lib/constants/addresses";
import type { CheckoutFormValues } from "./types";
import { useFormikContext } from "formik";
import type { CountryCode } from "@config/site/types";
import BaseInputField from "@components/commerce/baseForms/BaseInputField";
import { fieldStateHelper } from "@lib/utils/form/fieldStateHelper";
import { ShippingMethodTypes } from "@features/checkout/types";

export type BillingAddressSubFormProps = PropsWithChildren<{
    country: CountryCode;
    locale: string;
    isPickUpMethod?: boolean;
    countryChanged?: (country: CountryCode) => void;
}>;

const BillingAddressSubForm = ({
    country,
    countryChanged,
    isPickUpMethod,
    locale,
    children,
}: BillingAddressSubFormProps) => {
    const {
        values: checkoutFormValues,
        touched: checkoutFormTouched,
        errors: checkoutFormErrors,
        setFieldValue,
    } = useFormikContext<CheckoutFormValues>();
    const values = checkoutFormValues.billingAddress;
    const touched = checkoutFormTouched.billingAddress;
    const errors = checkoutFormErrors.billingAddress;

    const savedCountryValue = useRef(values?.country);
    const { get: getMicrocopy } = useMicrocopy();
    const focusRef = useCallback((node) => {
        if (node !== null && !isPickUpMethod) {
            node.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, []);

    const headerLabels = {
        billingAddress: getMicrocopy(CommonCMS.page, Checkout.step2.billingAddress),
        country: getMicrocopy(CommonCMS.page, Checkout.step2.formCountry.title),
        customerNumber: getMicrocopy(CommonCMS.page, Address.form.customerNumber),
    };

    const headerErrors = {
        countryExists: getMicrocopy(CommonCMS.page, Address.error.countryExists),
    };

    const isCountryChangeForBillingAddressAllowed =
        activeSiteConfig.isCountryChangeForBillingAddressAllowed;
    const hasDHLExtraFields = activeSiteConfig.checkout?.hasDHLExtraFields;

    useEffect(() => {
        if (!isCountryChangeForBillingAddressAllowed) {
            setFieldValue("billingAddress.country", activeSiteConfig.country);
        }
    }, []);

    const getIconProps = (field: string): { rightIcon?: "ok" } => {
        const isSuccess = !errors?.[field] && touched?.[field];
        if (isSuccess) {
            return {
                rightIcon: "ok",
            };
        }
        return {};
    };

    const { getFieldStateProps } = fieldStateHelper({ errors, touched });

    const renderCustomerNumberField = () =>
        checkoutFormValues.deliveryMethodId === ShippingMethodTypes.PARCEL_SHOP &&
        hasDHLExtraFields ? (
            <Box mt={4} mb={6}>
                <BaseInputField
                    required={true}
                    {...getFieldStateProps("customerNumber")}
                    {...getIconProps("customerNumber")}
                    id={`${BILLING_PREFIX}.customerNumber`}
                    header={headerLabels.customerNumber}
                    testId={`${BILLING_PREFIX}-customerNumber`}
                />
            </Box>
        ) : null;

    return (
        <>
            <Divider ref={focusRef} borderWidth={1} borderColor="gray.100" my={[6, 8]} />
            <Box sx={styles.fieldsWrapper} data-testid="billingaddress-stepform">
                {renderCustomerNumberField()}

                <Text fontWeight={"semibold"}>{headerLabels.billingAddress}</Text>

                <Box sx={styles.fieldWrapper} w="100%">
                    <BaseInputField
                        id={"billingAddress.country"}
                        {...getFieldStateProps("country")}
                        {...getIconProps("country")}
                        required={true}
                        header={headerLabels.country}
                        validate={async (value) => {
                            if (!isCountryChangeForBillingAddressAllowed) {
                                return false;
                            }

                            const result = await validateCountry(value);
                            const lastCountry = savedCountryValue.current;
                            if (result) {
                                if (!getStatesByCountry(lastCountry)) {
                                    values.region = "";
                                }
                                if (lastCountry !== result) {
                                    values.region = "";
                                    savedCountryValue.current = result as CountryCode;
                                }
                                if (values.country !== result) {
                                    countryChanged(result as CountryCode);
                                    values.country = result as CountryCode;
                                }
                                return false;
                            }
                            return headerErrors.countryExists;
                        }}
                        isReadOnly={!isCountryChangeForBillingAddressAllowed}
                    />
                </Box>
                <AddressFormFields
                    prefix={BILLING_PREFIX}
                    country={country}
                    locale={locale}
                    isBillingForm={true}
                    deliveryMethodId={checkoutFormValues.deliveryMethodId}
                />
                {children}

                <Box sx={styles.fieldWrapper}>&#160;</Box>
            </Box>
        </>
    );
};

export default BillingAddressSubForm;
